import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, Plane, BadgeCheck, CreditCard } from "lucide-react";
import { MaskedHeadline } from "./motion";

const HERO_IMG = "/machine.png";

const FEATURES = [
  { icon: Clock, t: "24/7 access", d: "Available across", d2: "every flight window" },
  { icon: Plane, t: "Travel-ready", d: "Convenient formats", d2: "for the journey" },
  { icon: BadgeCheck, t: "Compliance-led", d: "Assortment reviewed", d2: "before placement" },
  { icon: CreditCard, t: "Cashless & secure", d: "Tap, pay", d2: "and go" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.02, 1.08]);

  const smoothTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden pt-24 md:pt-28" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <motion.img
          src={HERO_IMG}
          alt="MinisRus TCN-ELV-11G automated retail machine at a high-traffic public space"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ y: yImg, scale: scaleImg }}
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/15 to-ink/70" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pt-6 md:pt-14 pb-20 md:pb-28 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 xl:col-span-6">
          <h1 className="font-display font-light text-bone leading-[1.02] tracking-tight text-[11vw] sm:text-[8vw] lg:text-[5vw] xl:text-[4.4vw]" data-testid="hero-headline">
            <span className="block"><MaskedHeadline text="Traveller essentials," delayBase={0.2} /></span>
            <span className="block text-orange">
              <MaskedHeadline text="ready around the clock." delayBase={0.5} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-6 max-w-xl text-bone/85 text-base md:text-lg leading-relaxed font-sans"
            data-testid="hero-subcopy"
          >
            MinisRus is a fully managed, cashless automated retail concept for airports, universities, student accommodation and other high-traffic locations — offering personal care, hygiene, family essentials and selected compliant wellbeing products.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 md:gap-6 max-w-2xl"
          >
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="flex flex-col items-start gap-3" data-testid={`hero-feature-${f.t.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <div className="w-11 h-11 md:w-12 md:h-12 rounded-full border border-orange/70 bg-orange/10 flex items-center justify-center">
                    <Icon size={19} strokeWidth={1.7} className="text-orange" />
                  </div>
                  <div>
                    <p className="text-bone text-[14px] md:text-[15px] font-semibold leading-tight">{f.t}</p>
                    <p className="text-bone/60 text-xs mt-1 leading-snug">{f.d}<br/>{f.d2}</p>
                  </div>
                </div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-3 md:gap-4"
          >
            <button
              onClick={() => smoothTo('#collection')}
              className="group inline-flex items-center gap-2 h-12 px-6 md:px-7 bg-orange text-ink hover:bg-orange-soft rounded-full text-[14px] font-semibold transition-colors"
              data-testid="hero-explore-btn"
            >
              Explore the Essentials
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
            <button
              onClick={() => smoothTo('#airport-pilot')}
              className="group inline-flex items-center gap-2 h-12 px-6 md:px-7 border border-bone/40 hover:border-orange text-bone hover:text-orange rounded-full text-[14px] font-medium transition-colors"
              data-testid="hero-concept-btn"
            >
              Request a Pilot
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
